import {
  Alert,
  Backdrop,
  Box,
  Button,
  ButtonBase,
  Chip,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/system";
import { useFormState } from "../../hooks/use-form-state";
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link as RouterLink } from "react-router-dom";
import { useGetDesignerBioQuery } from "../../hooks/use-get-designer-bio";
import { useUserContext } from "../../hooks/userContext";
import { useUpdateDesignerBioMutation } from "../../hooks/use-update-designer-bio-mutation";
import { useCreateDesignerBioMutation } from "../../hooks/use-create-designer-bio-mutation";
import { useFirebaseImageUpload } from "../../components/firebase-image-upload/use-firebase-image-upload";
import { FirebasePaths } from "../../utils/firebase";
import { useDashboardFooter } from "../../contexts/DashboardFooterContext";
import { useDesignerOnboardingAccess } from "../../hooks/useDesignerOnboardingAccess";
import { useGetDesignerOnboardingStatusQuery } from "../../hooks/useGetDesignerOnboardingStatusQuery";
import { useGetDesignerProjectFiles } from "../../hooks/use-get-designer-project-files";
import { useUpdateProjectFileMutation } from "../../hooks/use-update-project-file-mutation";
import { useNavigate } from "react-router";
import { Pages } from "../../utils/route-paths";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DashboardHeader from "../../components/dashboard-header";
import {
  PopulatedProjectFile,
  PopulatedDesignerBio,
} from "../../__generated__/types";
import { VisuallyHiddenInput } from "../../components/firebase-image-upload";
import TinyMceEditor from "../../components/tinymce-editor";
import { useDesignerApplicationContext } from "../../hooks/designer-application-context";
import { useDesignerAvatar } from "../../hooks/useDesignerAvatar";
import {
  ReviewManagement,
  DesignerReview as ReviewRecord,
} from "../../components/review-management";
import { useNotification } from "../../contexts/NotificationContext";
import { HdgTextInput } from "../../components/hdg-text-input";
import { compressImage } from "../../utils/image-compression";

const ABOUT_ME_LIMIT = 1000;
const SECONDARY_LIMIT = 1000;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_COVER_IMAGE_INPUT_SIZE = 30 * 1024 * 1024; // 30MB before compression

const richTextToPlainText = (html: string): string => {
  if (!html) return "";
  if (typeof window === "undefined") {
    return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }
  const div = window.document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || div.innerText || "").trim();
};

const normalizeReviews = (
  incoming?: (Partial<ReviewRecord> | null | undefined)[]
): ReviewRecord[] =>
  (incoming ?? [])
    .filter((review): review is Partial<ReviewRecord> => Boolean(review))
    .map((review) => ({
      id:
        review.id ||
        `${Date.now().toString(36)}-${Math.random().toString(16).slice(2)}`,
      rating: typeof review.rating === "number" ? review.rating : 0,
      text: review.text || "",
      author: review.author || "",
      location: review.location || "",
      avatar: review.avatar || "",
      source:
        (review.source as ReviewRecord["source"]) === "google" ||
        (review.source as ReviewRecord["source"]) === "facebook"
          ? (review.source as ReviewRecord["source"])
          : "manual",
      createdAt: review.createdAt || new Date().toISOString(),
    }));

const initialState = {
  userName: "",
  yearsExperience: "",
  aboutMe: "",
  education: [] as string[],
  whyWorkWithMe: "",
  designAdvice: "",
  favoriteMovie: "",
  favoriteMusicGenre: "",
  favoriteBook: "",
  currentlyReading: "",
  reviews: [] as ReviewRecord[],
};

type BioImageState = {
  file?: File;
  local_url: string;
  _id?: string;
} | null;

type SelectableDesignerFile = {
  key: string;
  id: string;
  type: "project";
  title: string;
  image?: string | null;
  featuredOnBio: boolean;
};

const TYPE_LABELS: Record<SelectableDesignerFile["type"], string> = {
  project: "Project File",
};

export const MyBioPage = () => {
  const { setFooter, clearFooter } = useDashboardFooter();
  const navigate = useNavigate();

  const { user } = useUserContext();
  const { application } = useDesignerApplicationContext();
  const { isBioUnlocked, isOnboardingComplete } = useDesignerOnboardingAccess();
  const { getDesignerBio, loading, designerBio } = useGetDesignerBioQuery();
  const { loading: onboardingStatusLoading } = useGetDesignerOnboardingStatusQuery(user?.id);
  const { updateDesignerBio, loading: updateLoading } =
    useUpdateDesignerBioMutation();
  const { createDesignerBio, loading: createLoading } =
    useCreateDesignerBioMutation();
  const { getDesignerProjectFiles, designerProjectFiles, loading: loadingProjectFiles } =
    useGetDesignerProjectFiles();
  const { updateProjectFile } = useUpdateProjectFileMutation();
  const { showError, showSuccess } = useNotification();
  const theme = useTheme();
  const isPickerFullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { refreshAvatar } = useDesignerAvatar({ autoLoad: false });
  const [isSaving, setIsSaving] = useState(false);
  const [isCompressingCoverImage, setIsCompressingCoverImage] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [isSavingSelections, setIsSavingSelections] = useState(false);

  const { loading: imageLoading, onUpload } = useFirebaseImageUpload(
    FirebasePaths.BIO_IMAGE
  );

  const [bioImage, setBioImage] = useState<BioImageState>(null);
  const [coverImage, setCoverImage] = useState<BioImageState>(null);
  const [coverImagePosition, setCoverImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });

  // Route guard: redirect designers if the bio is still locked
  // Wait for onboarding status to load before checking access to avoid premature redirects
  useEffect(() => {
    // Don't redirect while data is still loading
    if (onboardingStatusLoading) {
      return;
    }
    
    // Only redirect if we've confirmed the user doesn't have access
    if (user?.accountType === "Designer" && !isBioUnlocked) {
      const timeoutId = setTimeout(() => {
        navigate(Pages.DASHBOARD);
      }, 750);

      return () => clearTimeout(timeoutId);
    }
  }, [user?.accountType, isBioUnlocked, onboardingStatusLoading, navigate]);

  const toFileList = (file: File): FileList => {
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    return dataTransfer.files;
  };

  const { state, errors, handleChange, handleSubmit, handleReset } = useFormState({
    initialState,
    validate: (formState) => {
      const validationErrors: Partial<Record<keyof typeof initialState, string>> = {};
      if (!richTextToPlainText(formState.aboutMe)) {
        validationErrors.aboutMe = "About Me is required";
      }
      return validationErrors;
    },
    onSubmit: async (formState) => {
      let uploadedImageId = bioImage?._id ? String(bioImage._id) : "";
      let uploadedCoverImageId = coverImage?._id ? String(coverImage._id) : "";

      // Handle profile image upload
      if (bioImage?.file) {
        const uploaded = await onUpload(toFileList(bioImage.file));
        if (uploaded?.length) {
          uploadedImageId = String(uploaded[0]._id);
          setBioImage({
            _id: uploaded[0]._id ? String(uploaded[0]._id) : "",
            local_url: uploaded[0].url as string,
          });
        }
      }

      // Handle cover image upload
      if (coverImage?.file) {
        const uploaded = await onUpload(toFileList(coverImage.file));
        if (uploaded?.length) {
          uploadedCoverImageId = String(uploaded[0]._id);
          setCoverImage({
            _id: uploaded[0]._id ? String(uploaded[0]._id) : "",
            local_url: uploaded[0].url as string,
          });
        }
      }

      const resolvedYearsExperience =
        defaultYearsExperience !== undefined
          ? defaultYearsExperience
          : formState.yearsExperience.trim() !== ""
          ? parseInt(formState.yearsExperience, 10)
          : 0;

      const payload = {
        userName: formState.userName.trim(),
        yearsExperience: Number.isFinite(resolvedYearsExperience)
          ? resolvedYearsExperience
          : 0, // Default to 0 if empty (backend requires a number)
        aboutMe: formState.aboutMe.trim(),
        education: formState.education,
        whyWorkWithMe: formState.whyWorkWithMe.trim(),
        designAdvice: formState.designAdvice.trim(),
        favoriteMovie: formState.favoriteMovie.trim(),
        favoriteMusicGenre: formState.favoriteMusicGenre.trim(),
        favoriteBook: formState.favoriteBook.trim(),
        currentlyReading: formState.currentlyReading.trim(),
        image: uploadedImageId || undefined,
        coverImage: uploadedCoverImageId || undefined,
        coverImagePositionX: coverImagePosition.x,
        coverImagePositionY: coverImagePosition.y,
        reviews: formState.reviews.map((review, index) => ({
          id:
            review.id ||
            `${Date.now().toString(36)}-${index}-${Math.random()
              .toString(16)
              .slice(2)}`,
          rating: review.rating,
          text: review.text.trim(),
          author: review.author.trim(),
          location: review.location?.trim() || "",
          avatar: review.avatar?.trim() || "",
          source: review.source,
          createdAt: review.createdAt || new Date().toISOString(),
        })),
      };

      if (designerBio?._id) {
        await updateDesignerBio({
          _id: designerBio._id,
          ...payload,
        });
      } else {
        await createDesignerBio({
          designerId: user?.id || "",
          ...payload,
        });
      }

      if (user?.id) {
        try {
          const refreshedBio = await getDesignerBio(user.id, true);
          hydrateBioFromServer(refreshedBio);
        } catch (refreshError) {
          console.error("Error refreshing designer bio after save:", refreshError);
        }
      }
    },
  });

  const [credentialInput, setCredentialInput] = useState<string>("");

  const addCredential = useCallback(() => {
    const trimmed = credentialInput.trim();
    if (!trimmed) return;

    const exists = (state.education || []).some(
      (entry) => entry.trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (exists) {
      setCredentialInput("");
      return;
    }

    handleChange("education")([...(state.education || []), trimmed]);
    setCredentialInput("");
  }, [credentialInput, handleChange, state.education]);

  const removeCredential = useCallback(
    (index: number) => {
      const next = [...(state.education || [])];
      next.splice(index, 1);
      handleChange("education")(next);
    },
    [handleChange, state.education]
  );

  const isMutating =
    isSaving ||
    loading ||
    updateLoading ||
    createLoading ||
    imageLoading ||
    isCompressingCoverImage;
  const showSavingOverlay = isSaving || imageLoading || isCompressingCoverImage;

  const handleSave = useCallback(async () => {
    if (isMutating) return;
    setIsSaving(true);
    try {
      const success = await handleSubmit();
      if (!success) {
        showError("Please complete the required fields before saving.");
        return;
      }
      await refreshAvatar(true);
      showSuccess("Bio saved successfully.");
      if (user?.accountType === "Designer" && !isOnboardingComplete) {
        navigate(Pages.DASHBOARD);
      }
    } catch (error) {
      showError("We couldn’t save your changes. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }, [handleSubmit, refreshAvatar, isMutating, showError, showSuccess, navigate, user?.accountType, isOnboardingComplete]);

  const handleViewAsHomeowner = useCallback(() => {
    if (!user?.id) {
      return;
    }
    navigate(
      Pages.DESIGNER_PREMIUM_PROFILE.replace(":designerId", user.id)
    );
  }, [navigate, user?.id]);

  const handleAvatarInput = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) {
      // Check file size (5MB max for profile image)
      if (file.size > MAX_IMAGE_SIZE) {
        showError("File size must be less than 5MB");
        return;
      }
      
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        showError("Please upload a JPG or PNG file");
        return;
      }
      
      setBioImage({
        file,
        local_url: URL.createObjectURL(file),
      });
    }
  };

  const handleCoverImageInput = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }

    // Check file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      showError("Please upload a JPG or PNG file");
      return;
    }

    if (file.size > MAX_COVER_IMAGE_INPUT_SIZE) {
      showError("Please upload an image smaller than 30MB.");
      return;
    }

    let processedFile = file;

    if (file.size > MAX_IMAGE_SIZE) {
      setIsCompressingCoverImage(true);
      try {
        const compressionResult = await compressImage(file, MAX_IMAGE_SIZE);
        processedFile = compressionResult.compressed;
        const originalSizeMB = (compressionResult.originalSize / (1024 * 1024)).toFixed(1);
        const compressedSizeMB = (compressionResult.compressedSize / (1024 * 1024)).toFixed(1);
        showSuccess(
          `Cover image optimized from ${originalSizeMB}MB to ${compressedSizeMB}MB.`,
          { duration: 3500 }
        );
      } catch (error: any) {
        showError(
          error?.message || "Unable to optimize this image. Please choose a smaller file."
        );
        return;
      } finally {
        setIsCompressingCoverImage(false);
      }
    }

    setCoverImage({
      file: processedFile,
      local_url: URL.createObjectURL(processedFile),
    });
    setCoverImagePosition({ x: 0, y: 0 }); // Reset to center when uploading new image
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setStartPos({ 
      x: e.clientX - coverImagePosition.x, 
      y: e.clientY - coverImagePosition.y 
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      const newX = e.clientX - startPos.x;
      const newY = e.clientY - startPos.y;
      
      // Allow movement in all directions with expanded constraints
      // Since we're showing the full image, we need more range for repositioning
      const maxX = 400; // Allow right movement
      const minX = -400; // Allow left movement
      const maxY = 200; // Allow down movement
      const minY = -200; // Allow up movement
      
      setCoverImagePosition({
        x: Math.max(minX, Math.min(maxX, newX)),
        y: Math.max(minY, Math.min(maxY, newY)),
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const selectionLimit = 5;
  const minimumFeaturedRequired = 5;
  const availablePickerItems = useMemo<SelectableDesignerFile[]>(() => {
    // Only allow Project Files to be added to bio and featured
    const projectEntries =
      (designerProjectFiles ?? [])
        .filter(
          (file): file is PopulatedProjectFile =>
            Boolean(file?._id) && file?.published !== false
        )
        .map((file) => ({
          key: `project:${file._id}`,
          id: file._id as string,
          type: "project" as const,
          title: file.name || "Untitled Project",
          image: file.images?.[0]?.url ?? null,
          featuredOnBio: Boolean(
            (file as { featuredOnBio?: boolean | null }).featuredOnBio
          ),
        })) ?? [];

    // Before & After and Design Boards are no longer available for bio selection
    return projectEntries;
  }, [designerProjectFiles]);

  const featuredDesignerFiles = useMemo(
    () => availablePickerItems.filter((item) => item.featuredOnBio).slice(0, 5),
    [availablePickerItems]
  );

  const placeholderCount = Math.max(0, 5 - featuredDesignerFiles.length);

  const featuredKeys = useMemo(
    () => availablePickerItems.filter((item) => item.featuredOnBio).map((item) => item.key),
    [availablePickerItems]
  );

  const selectedKeySet = useMemo(() => new Set(selectedKeys), [selectedKeys]);

  const selectionCount = selectedKeys.length;
  const selectionOverflow = selectionCount > selectionLimit;
  const minimumAvailable = Math.min(
    minimumFeaturedRequired,
    availablePickerItems.length
  );
  const meetsMinimumSelection = selectionCount >= minimumAvailable;
  const selectionStatusColor =
    !meetsMinimumSelection || selectionOverflow ? "error.main" : "text.secondary";
  const isPickerDataLoading = loadingProjectFiles;

  const refreshDesignerFiles = useCallback(
    async (shouldRefresh = false) => {
      if (!user?.id) {
        return;
      }
      // Only fetch project files since only they can be added to bio
      await getDesignerProjectFiles(user.id, shouldRefresh);
    },
    [
      user?.id,
      getDesignerProjectFiles,
    ]
  );

  const handleOpenPicker = useCallback(() => {
    setSelectedKeys(featuredKeys);
    setIsPickerOpen(true);
  }, [featuredKeys]);

  const handlePickerClose = useCallback(
    (_event?: unknown, reason?: "backdropClick" | "escapeKeyDown") => {
      if (isSavingSelections) {
        return;
      }
      setIsPickerOpen(false);
    },
    [isSavingSelections]
  );

  const handleToggleSelect = useCallback(
    (key: string) => {
      setSelectedKeys((prev) => {
        const exists = prev.includes(key);
        if (exists) {
          return prev.filter((item) => item !== key);
        }
        if (prev.length >= selectionLimit) {
          showError(`You can feature up to ${selectionLimit} designer files.`);
          return prev;
        }
        return [...prev, key];
      });
    },
    [selectionLimit, showError]
  );

  const handleSaveSelections = useCallback(async () => {
    const designerId = user?.id;
    if (!designerId) {
      showError("Unable to update featured projects without a designer account.");
      return;
    }

    if (!availablePickerItems.length) {
      showError("Upload a designer file before featuring it in your bio.");
      return;
    }

    if (
      availablePickerItems.length >= minimumFeaturedRequired &&
      selectedKeys.length < minimumFeaturedRequired
    ) {
      showError(`Select at least ${minimumFeaturedRequired} files to feature on your bio.`);
      return;
    }

    if (selectedKeys.length > selectionLimit) {
      showError(`You can feature up to ${selectionLimit} designer files.`);
      return;
    }

    setIsSavingSelections(true);

    try {
      const desiredSelections = new Set(selectedKeys);
      const updatePromises: Promise<unknown>[] = [];

      availablePickerItems.forEach((item) => {
        const shouldBeFeatured = desiredSelections.has(item.key);
        if (!!item.featuredOnBio === shouldBeFeatured) {
          return;
        }

        // Only project files can be featured on bio
        if (item.type === "project") {
          updatePromises.push(
            updateProjectFile({
              _id: item.id,
              featuredOnBio: shouldBeFeatured,
            })
          );
        }
      });

      if (updatePromises.length) {
        await Promise.all(updatePromises);
      }

      await refreshDesignerFiles(true);
      showSuccess("Featured designer files updated.");
      setIsPickerOpen(false);
    } catch (error) {
      showError("Unable to update featured files. Please try again.");
    } finally {
      setIsSavingSelections(false);
    }
  }, [
    availablePickerItems,
    minimumFeaturedRequired,
    refreshDesignerFiles,
    selectedKeys,
    selectionLimit,
    showError,
    showSuccess,
    updateProjectFile,
    user?.id,
  ]);

  const renderPickerItem = useCallback(
    (item: SelectableDesignerFile) => {
      const isSelected = selectedKeySet.has(item.key);
      const thumbnailStyles =
        item.image && item.image.trim() !== ""
          ? {
              backgroundImage: `url(${item.image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }
          : {
              backgroundImage:
                "linear-gradient(135deg, rgba(236,236,236,0.6), rgba(221,221,221,0.6))",
            };

      return (
        <ButtonBase
          key={item.key}
          onClick={() => handleToggleSelect(item.key)}
          sx={{
            width: "100%",
            borderRadius: 2,
            border: isSelected ? "2px solid #5C3BFF" : "1px solid #E5E5E5",
            padding: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 2,
            textAlign: "left",
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2,
              backgroundColor: "#F5F5F5",
              flexShrink: 0,
              ...thumbnailStyles,
            }}
          />
          <Stack spacing={0.5} flexGrow={1} alignItems="flex-start">
            <Typography
              variant="body1"
              fontWeight={600}
              sx={{ color: "#1E1E1E" }}
            >
              {item.title}
            </Typography>
            <Chip
              label={TYPE_LABELS[item.type]}
              size="small"
              sx={{
                backgroundColor: isSelected ? "rgba(92, 59, 255, 0.12)" : "#F1F1F1",
                color: "#5C3BFF",
                fontWeight: 600,
              }}
            />
          </Stack>
          <Checkbox
            checked={isSelected}
            color="success"
            sx={{
              pointerEvents: "none",
            }}
          />
        </ButtonBase>
      );
    },
    [handleToggleSelect, selectedKeySet]
  );

  const defaultUserName = useMemo(() => {
    const first = user?.firstName?.trim() ?? "";
    const last = user?.lastName?.trim() ?? "";
    return [first, last].filter(Boolean).join(" ");
  }, [user?.firstName, user?.lastName]);

  const defaultYearsExperience = useMemo(() => {
    const experiences =
      ((application as { experience?: any[] } | null)?.experience ?? []) as Array<{
        startDate?: string | null;
        endDate?: string | null;
        current?: boolean | null;
      }>;

    if (!experiences.length) return undefined;

    const ranges = experiences.reduce<Array<[number, number]>>(
      (acc, experience) => {
        const startDateString = experience?.startDate;
        if (!startDateString) return acc;

        const start = new Date(startDateString);
        if (Number.isNaN(start.getTime())) return acc;

        const endCandidate = experience?.current
          ? new Date()
          : experience?.endDate
          ? new Date(experience.endDate as string)
          : new Date();

        const end =
          endCandidate instanceof Date && !Number.isNaN(endCandidate.getTime())
            ? endCandidate
            : new Date();

        if (end.getTime() <= start.getTime()) {
          return acc;
        }

        acc.push([start.getTime(), end.getTime()]);
        return acc;
      },
      []
    );

    if (!ranges.length) return undefined;

    ranges.sort((a, b) => a[0] - b[0]);

    // Merge overlapping experience ranges to avoid double-counting.
    const merged: Array<[number, number]> = [];
    for (const [start, end] of ranges) {
      const last = merged[merged.length - 1];
      if (!last || start > last[1]) {
        merged.push([start, end]);
        continue;
      }
      if (end > last[1]) {
        last[1] = end;
      }
    }

    const monthMs = 1000 * 60 * 60 * 24 * 30.4375;
    const totalMonths = merged.reduce(
      (months, [start, end]) => months + (end - start) / monthMs,
      0
    );

    if (totalMonths <= 0) return undefined;

    const roundedYears = Math.round(totalMonths / 12);
    if (roundedYears > 0) return roundedYears;

    return totalMonths > 0 ? 1 : undefined;
  }, [application]);

  const hasLoadedBioRef = useRef(false);
  const hasAutoFilledRef = useRef({
    userName: false,
    yearsExperience: false,
  });
  const lastFetchedDesignerIdRef = useRef<string | null>(null);

  useEffect(() => {
    hasLoadedBioRef.current = false;
    hasAutoFilledRef.current = {
      userName: false,
      yearsExperience: false,
    };
    lastFetchedDesignerIdRef.current = null;
  }, [user?.id]);

  const hydrateBioFromServer = useCallback(
    (bio?: PopulatedDesignerBio | null) => {
      if (!bio) {
        const nextUserName = defaultUserName;
        const nextYearsExperience =
          defaultYearsExperience !== undefined
            ? String(defaultYearsExperience)
            : "";

        handleReset({
          userName: nextUserName,
          yearsExperience: nextYearsExperience,
          aboutMe: "",
          education: [],
          whyWorkWithMe: "",
          designAdvice: "",
          favoriteMovie: "",
          favoriteMusicGenre: "",
          favoriteBook: "",
          currentlyReading: "",
          reviews: [],
        });
        setBioImage(null);
        setCoverImage(null);

        if (nextUserName.trim() !== "") {
          hasAutoFilledRef.current.userName = true;
        }
        if (nextYearsExperience.trim() !== "") {
          hasAutoFilledRef.current.yearsExperience = true;
        }
        return;
      }

      const nextUserName =
        (bio.userName ?? "").trim() === ""
          ? defaultUserName
          : (bio.userName as string);
      const nextYearsExperience =
        defaultYearsExperience !== undefined
          ? String(defaultYearsExperience)
          : bio.yearsExperience !== null && bio.yearsExperience !== undefined
          ? String(bio.yearsExperience)
          : "";

      const normalizedReviewsFromServer = normalizeReviews(
        (bio.reviews ?? []) as (Partial<ReviewRecord> | null | undefined)[]
      );

      handleReset({
        userName: nextUserName,
        yearsExperience: nextYearsExperience,
        aboutMe: bio.aboutMe || "",
        education: (bio.education ?? []).filter(
          (item): item is string => typeof item === "string"
        ),
        whyWorkWithMe: bio.whyWorkWithMe || "",
        designAdvice: bio.designAdvice || "",
        favoriteMovie: bio.favoriteMovie || "",
        favoriteMusicGenre: bio.favoriteMusicGenre || "",
        favoriteBook: bio.favoriteBook || "",
        currentlyReading: bio.currentlyReading || "",
        reviews: normalizedReviewsFromServer,
      });

      if (nextUserName.trim() !== "") {
        hasAutoFilledRef.current.userName = true;
      }
      if (nextYearsExperience.trim() !== "") {
        hasAutoFilledRef.current.yearsExperience = true;
      }

      if (bio.image?.url) {
        setBioImage({
          _id: bio.image._id ? String(bio.image._id) : "",
          local_url: bio.image.url as string,
        });
      } else {
        setBioImage(null);
      }

      if (bio.coverImage && typeof bio.coverImage === "object" && bio.coverImage?.url) {
        const normalizePosition = (value: unknown) => {
          if (typeof value === "number" && Number.isFinite(value)) {
            return value;
          }
          const parsed = Number(value);
          return Number.isFinite(parsed) ? parsed : 0;
        };

        setCoverImage({
          _id: bio.coverImage._id ? String(bio.coverImage._id) : "",
          local_url: bio.coverImage.url as string,
        });
        setCoverImagePosition({
          x: normalizePosition(bio.coverImagePositionX),
          y: normalizePosition(bio.coverImagePositionY),
        });
      } else {
        setCoverImage(null);
      }
    },
    [
      defaultUserName,
      defaultYearsExperience,
      handleReset,
    ]
  );

  useEffect(() => {
    const fetchBio = async () => {
      if (!user?.id || hasLoadedBioRef.current) {
        return;
      }

      try {
        const bio = await getDesignerBio(user.id, true);
        hydrateBioFromServer(bio);
      } catch (error) {
        console.error("Error loading designer bio:", error);
      } finally {
        hasLoadedBioRef.current = true;
      }
    };

    fetchBio();
  }, [user?.id, getDesignerBio, hydrateBioFromServer]);

  useEffect(() => {
    if (designerBio?._id) {
      return;
    }

    const shouldSetName =
      !hasAutoFilledRef.current.userName &&
      defaultUserName &&
      defaultUserName.trim() !== "" &&
      state.userName.trim() === "";

    if (shouldSetName) {
      handleChange("userName")(defaultUserName);
      hasAutoFilledRef.current.userName = true;
    }

    const shouldSetYears =
      !hasAutoFilledRef.current.yearsExperience &&
      defaultYearsExperience !== undefined &&
      state.yearsExperience.trim() === "";

    if (shouldSetYears) {
      handleChange("yearsExperience")(String(defaultYearsExperience));
      hasAutoFilledRef.current.yearsExperience = true;
    }
  }, [
    designerBio?._id,
    defaultUserName,
    defaultYearsExperience,
    state.userName,
    state.yearsExperience,
    handleChange,
  ]);

  useEffect(() => {
    if (defaultYearsExperience === undefined) {
      return;
    }
    const nextYearsExperience = String(defaultYearsExperience);
    if (state.yearsExperience !== nextYearsExperience) {
      handleChange("yearsExperience")(nextYearsExperience);
      hasAutoFilledRef.current.yearsExperience = true;
    }
  }, [defaultYearsExperience, state.yearsExperience, handleChange]);

  const renderProjectCard = (item: SelectableDesignerFile) => {
    const previewImage = item.image;
    const categoryLabel = TYPE_LABELS[item.type];
    return (
      <Box
        key={item.key}
        sx={{
          width: 150,
          height: 228,
          borderRadius: 2,
          border: "1px solid #ECECEC",
          overflow: "hidden",
          backgroundColor: "#fff",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            width: 150,
            height: 150,
            backgroundColor: "#F3F3F3",
            backgroundImage: previewImage ? `url(${previewImage})` : "none",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <Box p={2} sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle2" fontWeight={600} noWrap>
            {item.title || "Untitled Project"}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: "block" }}
          >
            {categoryLabel}
          </Typography>
        </Box>
      </Box>
    );
  };


  useEffect(() => {
    if (!user?.id) {
      return;
    }
    if (lastFetchedDesignerIdRef.current === user.id) {
      return;
    }
    lastFetchedDesignerIdRef.current = user.id;
    refreshDesignerFiles();
  }, [refreshDesignerFiles, user?.id]);

  useEffect(() => {
    setFooter(
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end" sx={{ width: "100%" }}>
        <Button
          variant="outlined"
          onClick={handleSave}
          disabled={isMutating}
          startIcon={
            isMutating ? <CircularProgress size={18} sx={{ color: "#1E1E1E" }} /> : undefined
          }
          sx={{
            borderColor: "#1E1E1E",
            color: "#1E1E1E",
            px: 4,
          }}
        >
          Publish
        </Button>
        <Button
          variant="contained"
          color={"black" as any}
          onClick={handleSave}
          disabled={isMutating}
          startIcon={
            isMutating ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : undefined
          }
          sx={{ px: 4 }}
        >
          Save Changes
        </Button>
      </Stack>
    );
    return () => clearFooter();
  }, [setFooter, clearFooter, handleSave, isMutating]);

  return (
    <>
    <Box position="relative" className="my-bio-page" sx={{ 
      p: 0,
      '&.css-8zlaff': {
        paddingTop: '0 !important',
        paddingBottom: '0 !important',
        '@media (min-width: 900px)': {
          paddingTop: '0 !important',
          paddingBottom: '0 !important',
        }
      }
    }}>
      <DashboardHeader 
        title="Designer's Admin Bio"
        showBackButton={false}
        primaryAction={{
          label: "View As Homeowner",
          onClick: handleViewAsHomeowner,
          variant: "outlined"
        }}
      />
      <Stack spacing={3}>

        <Box
          className="content-block"
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 3,
          }}
        >
          <Box
            sx={{
              width: "100%",
            }}
          >
            <Typography variant="h6" fontWeight={600} mb={2}>
              Designer's Bio
            </Typography>

            {/* Cover Image Upload - Full Width at Top */}
            <Box mb={4}>
              <Box
                sx={{
                  width: "100%",
                  height: 208,
                  borderRadius: 2,
                  border: coverImage?.local_url
                    ? "1px solid #E8E8E8"
                    : "1px dashed #BDBDBD",
                  backgroundColor: "#F8F8F8",
                  overflow: "hidden",
                  position: "relative",
                }}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                {coverImage?.local_url ? (
                  <>
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        backgroundImage: `url(${coverImage.local_url})`,
                        backgroundSize: "cover",
                        backgroundPosition: `calc(50% + ${coverImagePosition.x}px) calc(50% + ${coverImagePosition.y}px)`,
                        backgroundRepeat: "no-repeat",
                        cursor: isDragging ? "grabbing" : "grab",
                        userSelect: "none",
                      }}
                      onMouseDown={handleMouseDown}
                    />
                    {/* Edit button - separate from drag area */}
                    <ButtonBase
                      component="label"
                      sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        zIndex: 3,
                        cursor: "pointer",
                      }}
                      disabled={isMutating}
                    >
                      <EditIcon sx={{ fontSize: 18, color: "#5C3BFF" }} />
                      <VisuallyHiddenInput
                        type="file"
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={handleCoverImageInput}
                      />
                    </ButtonBase>
                    {/* Drag instruction overlay */}
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 12,
                        left: 12,
                        backgroundColor: "rgba(255, 255, 255, 0.7)",
                        color: "white",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: "0.75rem",
                        zIndex: 2,
                        pointerEvents: "none",
                      }}
                    >
                      Drag to Reposition
                    </Box>
                  </>
                ) : (
                  <ButtonBase
                    component="label"
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    disabled={isMutating}
                  >
                    <Stack
                      spacing={2}
                      alignItems="center"
                      justifyContent="center"
                      sx={{ width: "100%", height: "100%" }}
                    >
                      <AddIcon fontSize="large" sx={{ color: "#9E9E9E" }} />
                      <Typography variant="body2" color="text.secondary">
                        Add Profile Cover Photo
                      </Typography>
                    </Stack>
                    <VisuallyHiddenInput
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleCoverImageInput}
                    />
                  </ButtonBase>
                )}
              </Box>
              <Typography
                variant="caption"
                sx={{ display: "block", mt: 1, color: "text.secondary" }}
              >
                JPG/PNG, optimized automatically when larger than 5MB.
              </Typography>
            </Box>

            {/* Profile Picture and User Info Row */}
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={3}
              alignItems={{ xs: "stretch", md: "flex-start" }}
              mb={4}
            >
              {/* Profile Picture */}
              <Box>
                <ButtonBase
                  component="label"
                  sx={{
                    width: 164,
                    height: 164,
                    borderRadius: 3,
                    border: bioImage?.local_url
                      ? "1px solid #E8E8E8"
                      : "1px dashed #BDBDBD",
                    backgroundColor: "#F8F8F8",
                    overflow: "hidden",
                    position: "relative",
                  }}
                  disabled={isMutating}
                >
                  {bioImage?.local_url ? (
                    <>
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          backgroundImage: `url(${bioImage.local_url})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          backgroundColor: "rgba(255, 255, 255, 0.9)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        }}
                      >
                        <EditIcon sx={{ fontSize: 16, color: "#5C3BFF" }} />
                      </Box>
                    </>
                  ) : (
                    <Stack
                      spacing={2}
                      alignItems="center"
                      justifyContent="center"
                      sx={{ width: "100%", height: "100%" }}
                    >
                      <AddIcon fontSize="large" sx={{ color: "#9E9E9E" }} />
                      <Typography variant="body2" color="text.secondary">
                        Add Profile Picture
                      </Typography>
                    </Stack>
                  )}
                  <VisuallyHiddenInput
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarInput}
                  />
                </ButtonBase>
              </Box>

              {/* User Info Fields */}
              <Stack spacing={3} flex={1} width="100%">
                <TextField
                  label="User Name"
                  value={state.userName}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#F5F5F5",
                    },
                  }}
                />
                <TextField
                  label="Number of Years as a Full Time Design Professional"
                  type="number"
                  value={state.yearsExperience}
                  inputProps={{ min: 0, readOnly: true }}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#F5F5F5",
                    },
                  }}
                />
              </Stack>
            </Stack>

            {/* About Me and Credentials & Recognition - Two Column Layout */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TinyMceEditor
                  label="About Me"
                  value={state.aboutMe}
                  onChange={(html, _text) => handleChange("aboutMe")(html)}
                  maxChars={ABOUT_ME_LIMIT}
                  required
                  error={Boolean(errors.aboutMe)}
                  helperText={errors.aboutMe}
                  toolbar="undo redo | blocks | bold italic underline | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent"
                  plugins="advlist lists"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} mb={1}>
                    Credentials & Recognition
                  </Typography>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {(state.education || []).map((item, index) => (
                        <Chip
                          key={`${item}-${index}`}
                          label={item}
                          onDelete={() => removeCredential(index)}
                          sx={{
                            py: 0.5,
                            px: 0.5,
                            minWidth: "auto",
                            width: "fit-content",
                            borderRadius: 50,
                          }}
                        />
                      ))}
                    </Stack>
                    <HdgTextInput
                      label="Credentials & Recognition"
                      value={credentialInput}
                      onChange={setCredentialInput}
                      onSubmit={addCredential}
                      name="education"
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          paddingRight: 0,
                        },
                        "& .MuiOutlinedInput-input": {
                          padding: "14px 16px",
                        },
                      }}
                      endDecorator={
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={addCredential}
                          disabled={
                            !credentialInput.trim() ||
                            (state.education || []).some(
                              (entry) =>
                                entry.trim().toLowerCase() ===
                                credentialInput.trim().toLowerCase()
                            )
                          }
                          size="small"
                          sx={{
                            m: 0,
                            height: 36,
                            minWidth: 88,
                            borderRadius: 10,
                            boxShadow: "none",
                            mr: 1.5,
                          }}
                        >
                          Add
                        </Button>
                      }
                    />
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
        >
          <Box
            className="content-block"
            sx={{ flex: 1, p: { xs: 3, md: 4 }, borderRadius: 4 }}
          >
            <TinyMceEditor
              label="Why Work With Me"
              value={state.whyWorkWithMe}
              onChange={(html, _text) => handleChange("whyWorkWithMe")(html)}
              maxChars={SECONDARY_LIMIT}
              toolbar="undo redo | blocks | bold italic underline | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent"
              plugins="advlist lists"
            />
          </Box>

          <Box
            className="content-block"
            sx={{ flex: 1, p: { xs: 3, md: 4 }, borderRadius: 4 }}
          >
            <TinyMceEditor
              label="My Best Design Advice"
              value={state.designAdvice}
              onChange={(html, _text) => handleChange("designAdvice")(html)}
              maxChars={SECONDARY_LIMIT}
              toolbar="undo redo | blocks | bold italic underline | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent"
              plugins="advlist lists"
            />
          </Box>
        </Stack>

        <Box
          className="content-block"
          sx={{ p: { xs: 3, md: 4 }, borderRadius: 4 }}
        >
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            Favorite Things
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                label="Favorite Movie"
                fullWidth
                value={state.favoriteMovie}
                onChange={(event) =>
                  handleChange("favoriteMovie")(event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Favorite Music Genre"
                fullWidth
                value={state.favoriteMusicGenre}
                onChange={(event) =>
                  handleChange("favoriteMusicGenre")(event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Favorite Book"
                fullWidth
                value={state.favoriteBook}
                onChange={(event) =>
                  handleChange("favoriteBook")(event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Currently Reading/Listening"
                fullWidth
                value={state.currentlyReading}
                onChange={(event) =>
                  handleChange("currentlyReading")(event.target.value)
                }
              />
            </Grid>
          </Grid>
        </Box>

        <Box
          className="content-block"
          sx={{ p: { xs: 3, md: 4 }, borderRadius: 4 }}
        >
          <ReviewManagement
            reviews={state.reviews}
            onReviewsChange={(next) =>
              handleChange("reviews")(normalizeReviews(next))
            }
          />
        </Box>

        <Box
          className="content-block"
          sx={{ p: { xs: 3, md: 4 }, borderRadius: 4 }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Project Files
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {!designerBio?._id || !availablePickerItems.length
                  ? "Save your bio and add Designer Files to enable this section"
                  : "Preview Only"}
              </Typography>
            </Box>
            <Button
              variant="text"
              onClick={handleOpenPicker}
              disabled={!designerBio?._id || !availablePickerItems.length}
              sx={{ 
                textTransform: "none", 
                color: (!designerBio?._id || !availablePickerItems.length) ? "text.disabled" : "#5C3BFF",
                cursor: (!designerBio?._id || !availablePickerItems.length) ? "not-allowed" : "pointer"
              }}
            >
              Edit
            </Button>
          </Stack>
          <Stack
            direction="row"
            flexWrap="wrap"
            sx={{ gap: { xs: "12px", md: "20px" } }}
          >
            {featuredDesignerFiles.map(renderProjectCard)}
            {Array.from({ length: placeholderCount }).map((_, index) => (
              <ButtonBase
                key={`placeholder-${index}`}
                onClick={handleOpenPicker}
                disabled={!designerBio?._id || !availablePickerItems.length}
                sx={{
                  width: 150,
                  height: 228,
                  borderRadius: 2,
                  border: "1px dashed #D9D9D9",
                  backgroundColor: "#FBFBFB",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  gap: 1,
                  opacity: (!designerBio?._id || !availablePickerItems.length) ? 0.5 : 1,
                  cursor: (!designerBio?._id || !availablePickerItems.length) ? "not-allowed" : "pointer",
                }}
              >
                <AddIcon sx={{ fontSize: 28, color: "#BDBDBD" }} />
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#9E9E9E" }}>
                  Add Project To Bio
                </Typography>
              </ButtonBase>
            ))}
         </Stack>
       </Box>
      </Stack>
    </Box>
    <Dialog
      open={isPickerOpen}
      onClose={handlePickerClose}
      fullScreen={isPickerFullScreen}
      fullWidth
      maxWidth="sm"
      keepMounted
      disableEscapeKeyDown={isSavingSelections}
    >
      <DialogTitle sx={{ fontWeight: 700, pr: isPickerFullScreen ? 3 : 4 }}>
        Feature Designer Files
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          px: isPickerFullScreen ? 2 : 4,
          py: isPickerFullScreen ? 2 : 3,
        }}
      >
        <Stack spacing={2}>
          <Typography
            variant="body2"
            sx={{ color: selectionStatusColor }}
          >
            {`Select up to ${selectionLimit} items to showcase on your premium profile. ${selectionCount}/${selectionLimit} selected.`}
            {!meetsMinimumSelection &&
              availablePickerItems.length >= minimumFeaturedRequired
              ? " Add more to reach the minimum of five featured items."
              : ""}
          </Typography>
          {isPickerDataLoading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 4,
              }}
            >
              <CircularProgress />
            </Box>
          ) : availablePickerItems.length ? (
            <Stack spacing={1.5}>
              {availablePickerItems.map(renderPickerItem)}
            </Stack>
          ) : (
            <Alert
              severity="info"
              action={
                <Button
                  color="inherit"
                  size="small"
                  component={RouterLink}
                  to={Pages.ADD_DESIGNER_FILE}
                >
                  Add File
                </Button>
              }
            >
              You haven’t uploaded any designer files yet. Add a project, before &amp;
              after, or design board to feature it here.
            </Alert>
          )}
          {availablePickerItems.length > 0 &&
            availablePickerItems.length < minimumFeaturedRequired && (
              <Alert severity="warning">
                Upload and feature at least {minimumFeaturedRequired} designer files to
                activate your premium bio experience. You currently have{" "}
                {availablePickerItems.length}.
              </Alert>
            )}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: isPickerFullScreen ? 2 : 4,
          py: isPickerFullScreen ? 1.5 : 2,
        }}
      >
        <Button
          component={RouterLink}
          to={Pages.MY_DESIGN_FILES}
          sx={{ mr: "auto" }}
          disabled={isSavingSelections}
        >
          Manage Files
        </Button>
        <Button onClick={handlePickerClose} disabled={isSavingSelections}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveSelections}
          disabled={isSavingSelections || isPickerDataLoading}
          sx={{ minWidth: 120 }}
        >
          {isSavingSelections ? "Saving..." : "Save Selection"}
        </Button>
      </DialogActions>
    </Dialog>
    <Backdrop
      open={showSavingOverlay}
      sx={{
        color: "#fff",
        zIndex: (theme) => theme.zIndex.drawer + 1200,
        backgroundColor: "rgba(0,0,0,0.25)",
      }}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
    </>
  );
};
